/**
 * Configurações › IA (Versão B do protótipo aprovado: pipeline visual com inspetor por etapa).
 *
 * SÓ o dono da IA (thiago@tdzain.com.br) vê e altera esta tela: chaves de API, modelos, limiares do
 * porteiro, tom, piloto, teto de custo e aprendizado. A barreira de verdade é a Edge Function
 * `ai-runtime` (403 `ai_owner_only`); o menu e a guarda de rota só evitam mostrar o que não se pode usar.
 *
 * Duas camadas, como as outras telas: `ConfiguracoesIAView` é pura (dados e ações por props, testável
 * em SSR) e `ConfiguracoesIA` liga aos hooks. Chaves de API NUNCA aparecem inteiras: o servidor só
 * devolve o fim mascarado; digitar uma nova substitui a anterior.
 */
import React, { useState, type ReactElement, type ReactNode } from "react";
import { KeyRound, ShieldCheck } from "lucide-react";
import type { AiMemoryItem, AiOverview, AiSecretStatus, AiSettings, AiTestResult } from "../api/ai-types.js";
import { Badge, Button, Card, CardHeader, PageHeader, controlClass } from "../ui/index.js";
import { StatusPanel } from "../components/StatusPanel.js";
import { useAiMemory, useAiMemoryCurate, useAiOverview, useAiSecretActions, useAiSecrets, useAiSettings, useAiSettingsSave, useAiTest } from "../queries.js";
import { AI_INTENTS } from "../../../../packages/ai-core/src/ai-settings.js";

export type PipelineNode = "entrada" | "regras" | "porteiro" | "ferramentas" | "resposta" | "validador" | "envio";

const NODES: Array<{ id: PipelineNode; step: string; title: string; hint: string; tone: "success" | "info" | "warning" | "brand" }> = [
  { id: "entrada", step: "1 · Entrada", title: "Mensagem recebida", hint: "Texto e áudio transcrito", tone: "success" },
  { id: "regras", step: "2 · Regras", title: "Pré-filtros", hint: "Tag, pausa, teto de custo", tone: "success" },
  { id: "porteiro", step: "3 · Porteiro", title: "Jev", hint: "Intenção e confiança", tone: "info" },
  { id: "ferramentas", step: "4 · Fatos", title: "Ferramentas", hint: "Preço, frete, pedido", tone: "info" },
  { id: "resposta", step: "5 · Escrita", title: "Resposta", hint: "Modelo e tom", tone: "info" },
  { id: "validador", step: "6 · Segurança", title: "Validador", hint: "Sem valor inventado", tone: "warning" },
  { id: "envio", step: "7 · Saída", title: "Modo de envio", hint: "Sombra · Sugestão · Piloto", tone: "brand" }
];

const INTENT_LABEL: Record<string, string> = { preco: "Preço", ficha_tecnica: "Ficha técnica", frete: "Frete", status_pedido: "Status do pedido", saudacao: "Saudação", reclamacao: "Reclamação", pedido_humano: "Pede atendente", outro: "Outro" };
const MODE_LABEL = { shadow: "Sombra", suggest: "Sugestão", pilot: "Piloto" } as const;
const SECRET_LABEL: Record<string, { title: string; hint: string }> = {
  OPENROUTER_API_KEY: { title: "OpenRouter", hint: "Toda chamada de IA do Hub passa por aqui (resposta, transcrição, memória)." },
  TYPESAFE_API_KEY: { title: "Jev (TypeSafe)", hint: "Porteiro de decisão rápida. Sem esta chave, um modelo rápido via OpenRouter faz o papel." }
};

const MODEL_SUGGESTIONS = ["typesafe/jev-router", "anthropic/claude-sonnet-5.5", "anthropic/claude-haiku-5.5", "anthropic/claude-opus-5.5", "google/gemini-3.5-flash", "google/gemini-3.5-flash-lite", "google/gemini-embedding-2", "voyageai/voyage-4-lite", "voyageai/voyage-4"];

const pct = (value: number) => `${Math.round(value * 100)}%`;
const usd = (value: number) => `US$ ${value.toFixed(2)}`;

function Range({ label, value, min, max, step, format, onChange, hint }: { label: string; value: number; min: number; max: number; step: number; format: (v: number) => string; onChange: (v: number) => void; hint?: string }): ReactElement {
  const id = `r-${label.replace(/\W+/g, "-")}`;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-bold text-ink">{label}</label>
      <div className="flex items-center gap-3">
        <input id={id} type="range" min={min} max={max} step={step} value={value} onChange={(event) => onChange(Number(event.target.value))} className="flex-1 accent-[var(--brand,#E31B23)]" />
        <output className="w-12 text-right text-sm font-black tabular-nums text-ink">{format(value)}</output>
      </div>
      {hint ? <span className="text-xs text-muted">{hint}</span> : null}
    </div>
  );
}

function ModelField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }): ReactElement {
  const id = `m-${label.replace(/\W+/g, "-")}`;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-bold text-ink">{label}</label>
      <input id={id} list="ai-models" className={controlClass} value={value} onChange={(event) => onChange(event.target.value.trim())} spellCheck={false} />
    </div>
  );
}

function Switch({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }): ReactElement {
  return (
    <button type="button" role="switch" aria-checked={checked} aria-label={label} onClick={() => onChange(!checked)} className={"relative h-6 w-11 shrink-0 rounded-pill transition-colors " + (checked ? "bg-success" : "bg-border-strong")}>
      <span className={"absolute top-0.5 h-5 w-5 rounded-pill bg-white transition-all " + (checked ? "left-[22px]" : "left-0.5")} />
    </button>
  );
}

// ---------------------------------------------------------------------------------------------------- chaves
export function SecretsCard({ secrets, onSave, onRemove, onTest, busy }: { secrets: AiSecretStatus[]; onSave: (name: string, value: string) => void; onRemove: (name: string) => void; onTest: (name: string) => Promise<{ ok: boolean; detail: string }>; busy?: boolean }): ReactElement {
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [results, setResults] = useState<Record<string, { ok: boolean; detail: string }>>({});
  return (
    <Card data-section="secrets">
      <CardHeader title="Chaves de API" description="Só você vê esta área. A chave é guardada no servidor e nunca volta inteira: aqui aparece só o final." actions={<Badge tone="neutral"><KeyRound className="h-3 w-3" aria-hidden focusable={false} />Somente o dono</Badge>} />
      <div className="grid gap-4 md:grid-cols-2">
        {secrets.map((secret) => {
          const meta = SECRET_LABEL[secret.name] ?? { title: secret.name, hint: "" };
          const value = drafts[secret.name] ?? "";
          const result = results[secret.name];
          return (
            <div key={secret.name} className="flex flex-col gap-2 rounded-panel border border-border p-4" data-secret={secret.name}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <b className="text-ink">{meta.title}</b>
                {secret.configured ? <Badge tone="success">{`configurada · ${secret.masked ?? ""}`}</Badge> : <Badge tone="warning">não configurada</Badge>}
              </div>
              <p className="m-0 text-xs text-muted">{meta.hint}</p>
              {secret.source === "env" ? <p className="m-0 text-xs text-muted">Vinda do ambiente do servidor. Salvar uma nova aqui passa a valer no lugar dela.</p> : null}
              <label className="sr-only" htmlFor={`key-${secret.name}`}>{`Nova chave ${meta.title}`}</label>
              <input id={`key-${secret.name}`} type="password" autoComplete="off" spellCheck={false} placeholder={secret.configured ? "Colar para substituir a chave" : "Colar a chave aqui"} className={controlClass} value={value} onChange={(event) => setDrafts({ ...drafts, [secret.name]: event.target.value })} />
              <div className="flex flex-wrap gap-2">
                <Button size="sm" disabled={busy || value.trim().length < 8} onClick={() => { onSave(secret.name, value.trim()); setDrafts({ ...drafts, [secret.name]: "" }); }}>Salvar chave</Button>
                <Button size="sm" variant="outline" disabled={!secret.configured} onClick={async () => setResults({ ...results, [secret.name]: await onTest(secret.name) })}>Testar</Button>
                {secret.configured && secret.source === "vault" ? <Button size="sm" variant="ghost" onClick={() => onRemove(secret.name)}>Remover</Button> : null}
              </div>
              {result ? <p role="status" className={"m-0 text-sm font-bold " + (result.ok ? "text-success" : "text-danger")}>{result.detail}</p> : null}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------------------------------- pipeline
function Inspector({ node, draft, set }: { node: PipelineNode; draft: AiSettings; set: (patch: (d: AiSettings) => void) => void }): ReactElement {
  const t = draft.thresholds;
  switch (node) {
    case "entrada":
      return (
        <div className="flex flex-col gap-3">
          <p className="m-0 text-sm text-muted">Mensagens chegam pelo WhatsApp, são gravadas e só então a IA decide. Áudio do cliente é transcrito por um modelo multimodal.</p>
          <ModelField label="Modelo de transcrição de áudio" value={draft.models.transcriber} onChange={(v) => set((d) => { d.models.transcriber = v; })} />
          <p className="m-0 text-xs text-muted">Grupos nunca são respondidos pela IA. Figurinha, foto e documento do cliente vão para humano.</p>
        </div>
      );
    case "regras":
      return (
        <div className="flex flex-col gap-4">
          <Range label="Pausa após resposta humana" value={draft.pauseHoursAfterHuman} min={0} max={48} step={1} format={(v) => `${v} h`} onChange={(v) => set((d) => { d.pauseHoursAfterHuman = v; })} hint="Vale para respostas pelo painel e pelo celular." />
          <div className="flex flex-col gap-1">
            <label htmlFor="budget" className="text-sm font-bold text-ink">Teto de custo por dia (US$)</label>
            <input id="budget" type="number" min={0} max={1000} step={0.5} className={controlClass} value={draft.dailyBudgetUsd} onChange={(event) => set((d) => { d.dailyBudgetUsd = Number(event.target.value); })} />
            <span className="text-xs text-muted">Ao atingir o teto a IA para e a conversa fica com a equipe. 0 = sem teto.</span>
          </div>
          <ul className="m-0 list-disc pl-5 text-sm text-muted"><li>IA ligada e fora de pausa</li><li>Conversa 1:1, com o atendimento em modo IA</li><li>Sem humano ativo na conversa</li></ul>
        </div>
      );
    case "porteiro":
      return (
        <div className="flex flex-col gap-4">
          <ModelField label="Modelo do porteiro (OpenRouter, quando não usa a chave do Jev)" value={draft.models.gatekeeper} onChange={(v) => set((d) => { d.models.gatekeeper = v; })} />
          <div className="flex flex-col gap-1">
            <label htmlFor="provider" className="text-sm font-bold text-ink">Quem decide</label>
            <select id="provider" className={controlClass} value={draft.gatekeeperProvider} onChange={(event) => set((d) => { d.gatekeeperProvider = event.target.value as AiSettings["gatekeeperProvider"]; })}>
              <option value="typesafe">Jev (API da TypeSafe) — recomendado</option>
              <option value="llm">Modelo rápido via OpenRouter</option>
            </select>
          </div>
          <Range label="“A IA pode responder?” mínimo" value={draft.gates.canAnswerMin} min={0.3} max={0.99} step={0.01} format={pct} onChange={(v) => set((d) => { d.gates.canAnswerMin = v; })} />
          <Range label="Urgência acima de … vai para humano" value={draft.gates.urgencyMax} min={0.1} max={0.95} step={0.01} format={pct} onChange={(v) => set((d) => { d.gates.urgencyMax = v; })} />
          <Range label="Frustração acima de … vai para humano" value={draft.gates.frustrationMax} min={0.1} max={0.95} step={0.01} format={pct} onChange={(v) => set((d) => { d.gates.frustrationMax = v; })} />
          <fieldset className="m-0 flex flex-col gap-3 border-0 p-0">
            <legend className="mb-1 text-sm font-black text-ink">Confiança mínima por assunto</legend>
            {(Object.keys(t) as Array<keyof typeof t>).map((key) => (
              <Range key={key} label={INTENT_LABEL[key] ?? key} value={t[key]} min={0.5} max={0.99} step={0.01} format={pct} onChange={(v) => set((d) => { d.thresholds[key] = v; })} hint={key === "status_pedido" ? "Só responde se o telefone confere com o pedido." : key === "preco" || key === "frete" ? "O valor sempre vem da ferramenta." : undefined} />
            ))}
          </fieldset>
          <p className="m-0 text-xs text-muted">{`Sempre vão para humano: ${AI_INTENTS.filter((i) => ["reclamacao", "pedido_humano", "outro"].includes(i)).map((i) => INTENT_LABEL[i]).join(", ")}.`}</p>
        </div>
      );
    case "ferramentas":
      return (
        <ul className="m-0 flex list-none flex-col gap-2 p-0 text-sm">
          {[["Preço e oferta vigente", "catálogo"], ["Ficha técnica e estoque", "catálogo"], ["Frete", "regra aprovada (grátis a partir de R$ 2.000)"], ["Status e rastreio do pedido", "pedidos, pelo telefone"], ["Respostas parecidas da equipe", "memória aprendida"], ["Chamar atendente", "sempre disponível"]].map(([name, source]) => (
            <li key={name} className="flex items-center justify-between gap-2 rounded-control bg-surface-soft px-3 py-2"><span className="text-ink">{name}</span><Badge tone="info">{source}</Badge></li>
          ))}
          <li className="text-xs text-muted">Frete abaixo de R$ 2.000 ainda não tem tabela aprovada: a IA pede o CEP e passa para a equipe, sem inventar valor.</li>
        </ul>
      );
    case "resposta":
      return (
        <div className="flex flex-col gap-4">
          <ModelField label="Modelo de resposta" value={draft.models.responder} onChange={(v) => set((d) => { d.models.responder = v; })} />
          <Range label="Formalidade" value={draft.tone.formality} min={0} max={100} step={5} format={(v) => String(v)} onChange={(v) => set((d) => { d.tone.formality = v; })} hint="Do informal ao formal." />
          <Range label="Tamanho da resposta" value={draft.tone.length} min={0} max={100} step={5} format={(v) => String(v)} onChange={(v) => set((d) => { d.tone.length = v; })} hint="Da curta à completa." />
          <Range label="Uso de emoji" value={draft.tone.emoji} min={0} max={100} step={5} format={(v) => String(v)} onChange={(v) => set((d) => { d.tone.emoji = v; })} />
          <Range label="Criatividade (temperatura)" value={draft.tone.temperature} min={0} max={0.4} step={0.05} format={(v) => v.toFixed(2)} onChange={(v) => set((d) => { d.tone.temperature = v; })} hint="Máximo 0,40 por segurança." />
          <div className="flex flex-col gap-1">
            <label htmlFor="identity" className="text-sm font-bold text-ink">Se perguntarem “você é robô?”</label>
            <select id="identity" className={controlClass} value={draft.identityPolicy} onChange={(event) => set((d) => { d.identityPolicy = event.target.value as AiSettings["identityPolicy"]; })}>
              <option value="admit">Admite ser a assistente virtual e oferece atendente (recomendado)</option>
              <option value="handoff">Passa para um humano</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="style" className="text-sm font-bold text-ink">Voz da Super2</label>
            <textarea id="style" className={controlClass + " min-h-24"} maxLength={4000} value={draft.styleGuide} onChange={(event) => set((d) => { d.styleGuide = event.target.value; })} placeholder="Ex.: Tratamos o cliente pelo primeiro nome. Assinamos como Equipe Super2. Nunca prometemos prazo sem consultar." />
          </div>
        </div>
      );
    case "validador":
      return (
        <ul className="m-0 flex list-none flex-col gap-2 p-0 text-sm">
          {[["Valor em R$ que não veio de ferramenta", "bloqueia"], ["Prazo em dias sem cotação ou pedido", "bloqueia"], ["Dizer que é uma pessoa", "bloqueia"], ["Resposta vazia ou longa demais", "bloqueia"]].map(([rule, action]) => (
            <li key={rule} className="flex items-center justify-between gap-2 rounded-control bg-surface-soft px-3 py-2"><span className="text-ink">{rule}</span><Badge tone="danger">{action}</Badge></li>
          ))}
          <li className="text-xs text-muted">Quando o validador barra, o rascunho vai para a equipe com o motivo. Estas regras são fixas, não configuráveis.</li>
        </ul>
      );
    case "envio":
      return (
        <div className="flex flex-col gap-4">
          <div role="group" aria-label="Modo de envio" className="grid grid-cols-3 gap-1 rounded-control bg-surface-soft p-1">
            {(["shadow", "suggest", "pilot"] as const).map((mode) => (
              <button key={mode} type="button" aria-pressed={draft.mode === mode} onClick={() => set((d) => { d.mode = mode; })} className={"min-h-9 rounded-control px-2 text-sm " + (draft.mode === mode ? "bg-surface font-extrabold text-ink shadow-s1" : "font-bold text-muted hover:text-ink")}>{MODE_LABEL[mode]}</button>
            ))}
          </div>
          <p className="m-0 text-sm text-muted">{{ shadow: "Rascunho só interno, para comparar com o que a equipe responde. Ideal nos 14 dias de aprendizado.", suggest: "O rascunho aparece na conversa e a equipe aprova ou edita.", pilot: "Envia sozinha, em partes e com “digitando…”, só para conversas com a tag ou número abaixo." }[draft.mode]}</p>
          <div className="flex flex-col gap-1">
            <label htmlFor="pilot-tag" className="text-sm font-bold text-ink">Tag de ativação</label>
            <input id="pilot-tag" className={controlClass} value={draft.pilot.tag} maxLength={24} onChange={(event) => set((d) => { d.pilot.tag = event.target.value.trim(); })} />
            <span className="text-xs text-muted">Aplique esta tag a uma conversa (no topo do chat) para a IA responder nela.</span>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="pilot-numbers" className="text-sm font-bold text-ink">Números liberados (um por linha)</label>
            <textarea id="pilot-numbers" className={controlClass + " min-h-20 font-mono"} value={draft.pilot.numbers.join("\n")} onChange={(event) => set((d) => { d.pilot.numbers = event.target.value.split(/\n+/).map((line) => line.trim()).filter(Boolean); })} placeholder="5511999990001" />
            <span className="text-xs text-muted">Você e a diretoria. O número é comparado sem formatação.</span>
          </div>
        </div>
      );
  }
}

export interface ConfiguracoesIAViewProps {
  settings: AiSettings;
  overview?: AiOverview;
  secrets?: AiSecretStatus[];
  memory?: AiMemoryItem[];
  saving?: boolean;
  saveError?: string | null;
  dirty?: boolean;
  testResult?: AiTestResult | null;
  testError?: string | null;
  testing?: boolean;
  initialNode?: PipelineNode;
  onChange: (patch: (draft: AiSettings) => void) => void;
  onSave: () => void;
  onToggleEnabled: (enabled: boolean) => void;
  onSecretSave: (name: string, value: string) => void;
  onSecretRemove: (name: string) => void;
  onSecretTest: (name: string) => Promise<{ ok: boolean; detail: string }>;
  onCurate: (id: string, status: "active" | "pinned" | "rejected") => void;
  onTest: (text: string) => void;
}

export function ConfiguracoesIAView(props: ConfiguracoesIAViewProps): ReactElement {
  const { settings, overview } = props;
  const [node, setNode] = useState<PipelineNode>(props.initialNode ?? "porteiro");
  const [testText, setTestText] = useState("Quanto custa o fone Pro X?");
  const learning = overview?.learning;
  const agreement = overview?.drafts.avgAgreement;
  const providers = overview?.providers;
  return (
    <div className="flex flex-col gap-6" data-page="configuracoes-ia">
      <PageHeader
        title="Configurações › IA"
        description="Assistente de atendimento: chaves, modelos, porteiro, tom e controle de quando ela responde. Toda chamada de IA passa pelo OpenRouter."
        actions={
          <>
            <span className="inline-flex items-center gap-2 text-sm font-extrabold text-ink"><Switch checked={settings.enabled} onChange={props.onToggleEnabled} label="Interruptor geral da IA" />{settings.enabled ? "IA ligada" : "IA desligada"}</span>
            {settings.enabled ? <Button variant="danger" size="sm" onClick={() => props.onToggleEnabled(false)}>Desligar agora</Button> : null}
          </>
        }
      />
      <datalist id="ai-models">{MODEL_SUGGESTIONS.map((model) => <option key={model} value={model} />)}</datalist>

      <div className="flex flex-wrap items-center gap-2" data-section="status">
        <Badge tone={settings.enabled ? "success" : "warning"}>{settings.enabled ? "IA ligada" : "IA desligada"}</Badge>
        <Badge tone="info">{`Modo ${MODE_LABEL[settings.mode]}`}</Badge>
        {learning?.started ? <Badge tone="info">{`Aprendizado: dia ${learning.day} de ${learning.days}${learning.finished ? " (concluído)" : ""}`}</Badge> : <Badge tone="neutral">Aprendizado começa ao ligar</Badge>}
        {overview ? <Badge tone="neutral">{`Gasto hoje ${usd(overview.spentTodayUsd)} de ${usd(settings.dailyBudgetUsd)}`}</Badge> : null}
        {providers ? <Badge tone={providers.openrouter ? "success" : "danger"}>{providers.openrouter ? "OpenRouter ok" : "OpenRouter sem chave"}</Badge> : null}
        {providers ? <Badge tone={providers.typesafe ? "success" : "neutral"}>{providers.typesafe ? "Jev ok" : "Jev sem chave"}</Badge> : null}
      </div>

      {props.secrets ? <SecretsCard secrets={props.secrets} onSave={props.onSecretSave} onRemove={props.onSecretRemove} onTest={props.onSecretTest} /> : null}

      <Card data-section="pipeline">
        <CardHeader title="Caminho de cada mensagem" description="Clique numa etapa para ajustar. As alterações só valem depois de Salvar." actions={<Button onClick={props.onSave} disabled={!props.dirty || props.saving}>{props.saving ? "Salvando…" : "Salvar alterações"}</Button>} />
        {props.saveError ? <p role="alert" className="mb-3 rounded-control bg-danger-soft px-3 py-2 text-sm font-bold text-danger">{`Não foi possível salvar: ${props.saveError}`}</p> : null}
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_24rem]">
          <ol className="m-0 grid list-none gap-2 p-0 sm:grid-cols-2" aria-label="Etapas do pipeline">
            {NODES.map((n) => (
              <li key={n.id}>
                <button type="button" onClick={() => setNode(n.id)} aria-pressed={node === n.id} data-node={n.id} className={"flex w-full flex-col items-start gap-0.5 rounded-panel border-2 p-3 text-left transition-colors " + (node === n.id ? "border-brand bg-brand-wash" : "border-border bg-surface hover:border-border-strong")}>
                  <span className="text-[11px] font-black uppercase tracking-wider text-muted">{n.step}</span>
                  <b className="text-base text-ink">{n.title}</b>
                  <span className="text-xs text-muted">{n.hint}</span>
                </button>
              </li>
            ))}
          </ol>
          <aside aria-label="Ajustes da etapa" className="rounded-panel border border-border bg-surface p-4" data-inspector={node}>
            <Inspector node={node} draft={settings} set={props.onChange} />
          </aside>
        </div>
      </Card>

      <Card data-section="aprendizado">
        <CardHeader title="Aprendizado" description="A IA lê o histórico e cada resposta nova da equipe e monta uma biblioteca de exemplos. Ela não é treinada: consulta exemplos parecidos na hora de escrever." />
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[
            ["Exemplos aprendidos", String(overview?.memory.total ?? "—")],
            ["Aprovados por você", String(overview?.memory.pinned ?? "—")],
            ["Rascunhos nos últimos 14 dias", String(overview?.drafts.total ?? "—")],
            ["Concordância com a equipe (meta 85%)", agreement == null ? "—" : pct(agreement)]
          ].map(([label, value]) => (
            <div key={label} className="rounded-panel bg-surface-soft p-3"><div className="text-xs text-muted">{label}</div><div className="text-2xl font-black text-ink">{value}</div></div>
          ))}
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <ModelField label="Modelo de embeddings (busca de exemplos)" value={settings.models.embedder} onChange={(v) => props.onChange((d) => { d.models.embedder = v; })} />
          <ModelField label="Modelo de resumo" value={settings.models.summarizer} onChange={(v) => props.onChange((d) => { d.models.summarizer = v; })} />
        </div>
        <p className="mt-2 text-xs text-muted">Trocar o modelo de embeddings exige reaprender o histórico (as dimensões mudam).</p>
        <h3 className="mb-2 mt-5 text-base font-black text-ink">Curadoria dos exemplos recentes</h3>
        {!props.memory?.length ? <p className="m-0 text-sm text-muted">Nenhum exemplo aprendido ainda. Rode o aprendizado do histórico (npm run ai:learn) ou aguarde as próximas respostas da equipe.</p> : (
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {props.memory.slice(0, 10).map((item) => (
              <li key={item.id} className="grid gap-2 rounded-panel border border-border p-3 sm:grid-cols-[1fr_auto]" data-memory-id={item.id}>
                <div className="min-w-0 text-sm"><b className="text-ink">{`“${item.question}”`}</b><div className="text-muted">{`→ ${item.answer}`}</div>{item.status !== "active" ? <Badge tone={item.status === "pinned" ? "success" : "danger"}>{item.status === "pinned" ? "aprovado" : "rejeitado"}</Badge> : null}</div>
                <div className="flex gap-2"><Button size="sm" variant="outline" onClick={() => props.onCurate(item.id, "pinned")}>Aprovar</Button><Button size="sm" variant="ghost" onClick={() => props.onCurate(item.id, "rejected")}>Rejeitar</Button></div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card data-section="teste">
        <CardHeader title="Testar o pipeline" description="Roda a mensagem pelo porteiro, ferramentas e validador com as configurações salvas. Nada é enviado a ninguém." />
        <div className="flex flex-col gap-2 sm:flex-row">
          <label className="sr-only" htmlFor="ai-test">Mensagem de teste</label>
          <input id="ai-test" className={controlClass + " flex-1"} value={testText} onChange={(event) => setTestText(event.target.value)} maxLength={500} />
          <Button onClick={() => props.onTest(testText)} disabled={props.testing || !testText.trim()}>{props.testing ? "Testando…" : "Testar"}</Button>
        </div>
        {props.testError ? <p role="alert" className="mt-3 rounded-control bg-danger-soft px-3 py-2 text-sm font-bold text-danger">{props.testError}</p> : null}
        {props.testResult ? <TestResult result={props.testResult} /> : null}
      </Card>
    </div>
  );
}

function TestResult({ result }: { result: AiTestResult }): ReactElement {
  const d = result.decision;
  const gate = d.gatekeeper;
  const label: Record<string, string> = { draft: "Responderia com rascunho", observe: "Rascunho interno (sombra)", send: "Enviaria", handoff: "Passaria para um humano", skip: "Não agiria" };
  const row = (name: string, value: ReactNode) => <div className="flex justify-between gap-3 border-b border-border py-1.5 text-sm last:border-0"><span className="text-muted">{name}</span><b className="text-right text-ink">{value}</b></div>;
  return (
    <div className="mt-3 flex flex-col gap-2" data-test-result={d.action}>
      <p className="m-0 flex items-center gap-2 text-base font-black text-ink"><ShieldCheck className="h-5 w-5" aria-hidden focusable={false} />{label[d.action] ?? d.action}{d.reason ? <Badge tone="warning">{d.reason}</Badge> : null}</p>
      {gate ? <div className="rounded-panel bg-surface-soft px-3">{row("Intenção", `${INTENT_LABEL[gate.intent ?? "outro"] ?? gate.intent} · ${pct(gate.intentConfidence ?? 0)}`)}{row("Pode responder", pct(gate.canAnswer ?? 0))}{row("Urgência / frustração", `${pct(gate.urgency ?? 0)} / ${pct(gate.frustration ?? 0)}`)}{row("Decidiu", `${gate.provider === "typesafe" ? "Jev" : gate.provider === "llm" ? "modelo rápido" : "regras"}${gate.latencyMs ? ` · ${gate.latencyMs} ms` : ""}`)}</div> : null}
      {d.draft ? <blockquote className="m-0 rounded-panel border border-dashed border-border p-3 text-sm text-ink">{d.draft}</blockquote> : null}
      {d.detail ? <p className="m-0 text-xs text-muted">{d.detail}</p> : null}
      {d.facts?.length ? <p className="m-0 flex flex-wrap gap-1">{d.facts.map((fact, index) => <Badge key={index} tone={fact.ok ? "info" : "warning"}>{`${fact.ok ? "fonte" : "sem dado"}: ${fact.tool}`}</Badge>)}</p> : null}
      {result.gatekeeperFailures.length ? <p className="m-0 text-xs text-warning">{`Porteiro preferido falhou (${result.gatekeeperFailures.join(", ")}); outro assumiu.`}</p> : null}
      {d.costUsd ? <p className="m-0 text-xs text-muted">{`Custo desta rodada: US$ ${d.costUsd.toFixed(4)}`}</p> : null}
    </div>
  );
}

/** Rota `/configuracoes/ia`: carrega e liga aos hooks. Só o dono passa (o servidor responde 403 aos demais). */
export function ConfiguracoesIA(): ReactElement {
  const settingsQuery = useAiSettings();
  const overview = useAiOverview();
  const secrets = useAiSecrets();
  const memory = useAiMemory();
  const save = useAiSettingsSave();
  const secretActions = useAiSecretActions();
  const curate = useAiMemoryCurate();
  const test = useAiTest();
  const [draft, setDraft] = useState<AiSettings | null>(null);

  if (settingsQuery.isError) {
    const forbidden = /:403$/.test(settingsQuery.error.message) || /ai_owner_only/.test(settingsQuery.error.message);
    return (
      <div className="flex flex-col gap-6" data-page="configuracoes-ia">
        <PageHeader title="Configurações › IA" />
        <StatusPanel
          kind="error"
          title={forbidden ? "Área restrita ao dono da IA" : "Não foi possível carregar as configurações da IA"}
          detail={forbidden ? "Só o usuário responsável pela IA vê e altera as chaves e os parâmetros do assistente." : /:404$/.test(settingsQuery.error.message) ? "A função do assistente de IA ainda não foi publicada neste ambiente." : "Tente novamente em instantes."}
        />
      </div>
    );
  }
  if (!settingsQuery.data) return <p className="m-0 text-sm text-muted" aria-busy="true">Carregando…</p>;

  const current = draft ?? settingsQuery.data;
  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(settingsQuery.data);
  return (
    <ConfiguracoesIAView
      settings={current}
      overview={overview.data}
      secrets={secrets.data}
      memory={memory.data}
      dirty={dirty}
      saving={save.isPending}
      saveError={save.isError ? save.error.message.split(":")[0] ?? "erro" : null}
      testing={test.isPending}
      testResult={test.data ?? null}
      testError={test.isError ? (/503/.test(test.error.message) || /openrouter_not_configured/.test(test.error.message) ? "Configure a chave do OpenRouter para testar." : "O teste falhou. Veja as chaves e tente de novo.") : null}
      onChange={(patch) => { const next = structuredClone(current); patch(next); setDraft(next); }}
      onSave={() => { if (draft) save.mutate(draft as unknown as Record<string, unknown>, { onSuccess: () => setDraft(null) }); }}
      onToggleEnabled={(enabled) => save.mutate({ enabled }, { onSuccess: () => setDraft(null) })}
      onSecretSave={(name, value) => secretActions.save.mutate({ name, value })}
      onSecretRemove={(name) => secretActions.remove.mutate(name)}
      onSecretTest={(name) => secretActions.test.mutateAsync(name).catch(() => ({ ok: false, detail: "Não foi possível testar agora." }))}
      onCurate={(id, status) => curate.mutate({ id, patch: { status } })}
      onTest={(text) => test.mutate(text)}
    />
  );
}

